import * as BP from '@fixtuur/backpack';
import { AnyAction, Dispatch } from 'redux';
import * as Core from '../../../../../store/core';
import { ResolveFurniture, getUserOptions, refineParticleTreeNode } from './furnitureResolution';
import newtech from '../furniture/newtech.json';

type Particle = BP.Api.Configuration.Particle.Particle;
type ParticleTreeNodeWithAttachments = BP.Render.Particle.Tree.ParticleTreeNodeWithAttachments;
type TagAttachmentOptions = BP.Render.Particle.Tree.TagAttachmentOptions;

export type FurnitureChoiceResult = {
    furnitureChoiceTree: ParticleTreeNodeWithAttachments;
    attachmentOptions: TagAttachmentOptions[];
};

export type ResolveFurnitureChoiceTree = (
    particles: Particle[],
) => Promise<FurnitureChoiceResult | null>;

// Define tenants that use Attachment Hotspots rules.
const hotspotRulesByTenant: Record<string, Record<string, Array<Record<string, string>>>> = {
    newtech: newtech,
};

// Get Attachment Hotspots ruleset for the given tenant.
const getHotspotRulesByTenant = (tenant: string) => {
    return hotspotRulesByTenant[tenant];
};

// See if the entire attachment proposal exists in ruleset
const proposalMatcher = (proposal: Record<string, string>, list: Record<string, string>[]) => {
    for (let i = 0; i < list.length; i++) {
        if (JSON.stringify(list[i]) === JSON.stringify(proposal)) {
            return true;
        }
    }
    return false;
};

const isSubset = (left: Record<string, string>, right: Record<string, string>) => {
    const leftKeys = Object.keys(left);
    const rightKeys = Object.keys(right);
    const includes = leftKeys.every(key => rightKeys.includes(key));

    if (includes) {
        return leftKeys.every(key => {
            const leftValue = left[key];
            const rightValue = right[key];
            return leftValue === rightValue;
        });
    }

    return false;
};

export async function resolveFurnitureChoiceTree(
    particles: Particle[],
    serviceConfig: BP.Api.ServiceConfig,
    dispatch: Dispatch<AnyAction>,
): Promise<FurnitureChoiceResult | null> {
    try {
        const furnitureChoiceTree = await ResolveFurniture(particles, serviceConfig);
        const attachmentOptions = getUserOptions(furnitureChoiceTree);
        return { furnitureChoiceTree, attachmentOptions };
    } catch (err: unknown) {
        dispatch(
            Core.error({
                message:
                    (err as any)?.message ??
                    `Failed to construct furniture from particles: ${particles}`,
                context: {
                    type: 'render',
                },
            }),
        );
        return null;
    }
}

export function applyAttachmentSelections(
    attachmentOptions: TagAttachmentOptions[],
    currentAttachmentSelections: Record<string, string>,
    attachmentProposals: Record<string, string>,
    furnitureChoiceTree: ParticleTreeNodeWithAttachments,
    tenant: string,
) {
    const attachmentSelections = resolveAttachmentSelectionsAndProposals(
        attachmentOptions,
        currentAttachmentSelections,
        attachmentProposals,
        tenant,
    );
    const furnitureTree = refineParticleTreeNode(furnitureChoiceTree, attachmentSelections);
    return { furnitureTree, attachmentSelections };
}

function resolveAttachmentSelectionsAndProposals(
    attachmentOptions: TagAttachmentOptions[],
    currentAttachmentSelections: Record<string, string>,
    attachmentProposals: Record<string, string>,
    tenant: string,
): Record<string, string> {
    // Ensure other hotspots stay the same by including current selections in our attachment proposals.
    const allAttachmentProposals = Object.assign(
        {},
        currentAttachmentSelections,
        attachmentProposals,
    );

    // Resolve attachment selections given our options and selections.
    const proposedAttachmentResolution = {
        ...(attachmentOptions
            ? attachmentOptions.reduce(
                  (s, opt) => ({
                      ...s,
                      [opt.tag]: opt.selected,
                  }),
                  {} as Record<string, string>,
              )
            : {}),
        ...Object.entries(allAttachmentProposals)
            .filter(([attachmentOption, value]) =>
                attachmentOptions?.find(a => a.tag === attachmentOption)?.options.includes(value),
            )
            .reduce(
                (s, [attachmentOption, value]) => ({
                    ...s,
                    [attachmentOption]: value,
                }),
                {} as Record<string, string>,
            ),
    };

    const attachmentHotspotRules = getHotspotRulesByTenant(tenant);

    /**
     * IF hotspot rules don't exist for the given tenant
     * OR IF we need to process or validate more hotspots than have been defined in the ruleset
     * OR IF the entire Proposed Attachment Resolution is allowed as per the ruleset
     *
     * THEN return Proposed Attachment Resolution
     */
    if (
        attachmentHotspotRules === undefined ||
        !Object.keys(attachmentHotspotRules).includes(attachmentOptions.length.toString()) ||
        proposalMatcher(
            proposedAttachmentResolution,
            attachmentHotspotRules[attachmentOptions.length],
        )
    ) {
        return proposedAttachmentResolution;
    }

    /**
     * IF hotspot rules exist
     * AND we have rules for the given amount of options
     * AND the Proposed Attachment Resolution has been rejected by the ruleset
     *
     * THEN return first legal hotspots combination given the latest user selection
     */
    return {
        ...attachmentHotspotRules[attachmentOptions.length].find(val =>
            isSubset(
                Object.keys(attachmentProposals).length > 0
                    ? attachmentProposals
                    : currentAttachmentSelections,
                val,
            ),
        ),
    } as Record<string, string>;
}
